import { DateTime } from "luxon";

import type {
  CustomDateRange,
  DateRangePreset,
  ExpandedDateRangePreset,
} from "@app/analytics/types";

export const RANGE_PRESETS: { [k in DateRangePreset]: string } = {
  0: "Today",
  7: "Last 7 days",
  30: "Last 30 days",
  60: "Last 60 days",
  90: "Last 90 days",
  180: "Last 6 months",
  365: "Last 12 months",
};

export interface DateSelection {
  startDate: DateTime;
  endDate: DateTime;
  compare: boolean;
  compareStartDate?: DateTime | null;
  compareEndDate?: DateTime | null;
}

export const isMaxDateRangePreset = (preset: DateRangePreset) =>
  preset === "365";

export const getStartAndEndDatesFromRangePreset = (
  preset: ExpandedDateRangePreset,
  endDate = DateTime.now()
) => {
  const days = parseInt(preset);

  let periodStart;

  if (days === 365) {
    periodStart = endDate.minus({ year: 1 }).startOf("day");
  } else if (days === 180) {
    periodStart = endDate.minus({ months: 6 }).startOf("day");
  } else if (days > 30) {
    periodStart = endDate.minus({ months: days / 30 }).startOf("day");
  } else {
    periodStart = endDate.minus({ days }).startOf("day");
  }
  const periodEnd = endDate.endOf("day");

  return { startDate: periodStart, endDate: periodEnd };
};

export const getDateSelection = ({
  preset,
  compare,
}: {
  preset: ExpandedDateRangePreset;
  endDate?: DateTime;
  compare?: boolean;
}): DateSelection => {
  const currentDate = getStartAndEndDatesFromRangePreset(preset);

  let compareDateStartEnd: {
    startDate: DateTime | null;
    endDate: DateTime | null;
  } = { startDate: null, endDate: null };
  if (compare) {
    compareDateStartEnd = getStartAndEndDatesFromRangePreset(
      preset,
      currentDate.startDate
    );
  }

  return {
    startDate: currentDate.startDate,
    endDate: currentDate.endDate,
    compare: compare ?? false,
    compareStartDate: compareDateStartEnd.startDate,
    compareEndDate: compareDateStartEnd.endDate,
  };
};

export const getDateLabel = (startDate: DateTime, endDate: DateTime) => {
  const isSameYear = startDate.year === endDate.year;

  const isSameDay = startDate.startOf("day").equals(endDate.startOf("day"));

  const startLabel = startDate.toLocaleString({
    month: "short",
    day: "numeric",
    year: isSameYear && !isSameDay ? undefined : "numeric",
  });

  if (isSameDay) return startLabel;

  const endLabel = endDate.toLocaleString({
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  return `${startLabel} - ${endLabel}`;
};

export const getDateLabels = ({
  comparing,
  customDateRange,
  rangePreset,
}: {
  comparing: boolean;
  customDateRange: CustomDateRange | null;
  rangePreset: DateRangePreset | null;
}) => {
  if (customDateRange) {
    const dateLabel = getDateLabel(...customDateRange);
    let compareDateLabel = null;
    if (comparing) {
      const [startDate, endDate] = customDateRange;
      const numDays = Math.ceil(endDate.diff(startDate).as("days"));

      compareDateLabel = getDateLabel(
        startDate.minus({ days: numDays + 1 }),
        endDate.minus({ days: numDays + 1 })
      );
    }

    return { dateLabel, compareDateLabel };
  }

  if (!rangePreset) {
    return { dateLabel: "Select a date range", compareDateLabel: null };
  }

  const { startDate, endDate } =
    getStartAndEndDatesFromRangePreset(rangePreset);
  const dateLabel = getDateLabel(startDate, endDate);

  let compareDateLabel = null;

  if (comparing) {
    const compareDates = getStartAndEndDatesFromRangePreset(
      rangePreset,
      startDate.minus({ day: 1 })
    );

    compareDateLabel = getDateLabel(
      compareDates.startDate,
      compareDates.endDate
    );
  }

  return { dateLabel, compareDateLabel };
};
